import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Loader2, TrendingUp, Building2, Wallet, ShieldAlert } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { FUNNEL_STAGES, stageLabel, formatChf } from "@/lib/sales";

function Kpi({ label, value, sub, testid, accent }) {
  return (
    <div data-testid={testid} className="card-surface rounded-xl px-4 py-3">
      <p className="text-xs text-fundio-muted">{label}</p>
      <p className={`mt-1 font-display text-2xl font-semibold ${accent || "text-fundio-ink"}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-fundio-faint">{sub}</p>}
    </div>
  );
}

export default function SalesDashboard() {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/sales/dashboard").then((r) => setD(r.data)).catch(() => setD(false)); }, []);

  return (
    <div data-testid="sales-dashboard-page">
      <PageHeader title={t("sales.dashboard.title")} subtitle={t("sales.dashboard.subtitle")} />

      <div data-testid="sales-preview-note" className="mb-5 flex items-center gap-2 rounded-lg border border-status-warning-border bg-status-warning-bg/50 px-4 py-2 text-xs text-status-warning">
        <ShieldAlert className="h-4 w-4" />
        <span><span className="font-semibold">{t("sales.dashboard.previewTag")}</span> · {t("sales.dashboard.previewNote")}</span>
      </div>

      {!d ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Kpi testid="sales-kpi-unique-active" label={t("sales.dashboard.uniqueActive")} value={d.unique_active_organizations} sub={t("sales.dashboard.uniqueActiveSub")} />
            <Kpi testid="sales-kpi-prio-a" label={t("sales.prio.A")} value={d.sales_priority.A} accent="text-status-danger" />
            <Kpi testid="sales-kpi-prio-b" label={t("sales.prio.B")} value={d.sales_priority.B} accent="text-status-warning" />
            <Kpi testid="sales-kpi-prio-c" label={t("sales.prio.C")} value={d.sales_priority.C} />
          </div>

          <Panel title={t("sales.dashboard.funnelTitle")} subtitle={t("sales.dashboard.funnelSub")} testid="sales-funnel-panel">
            <div className="grid grid-cols-2 gap-3 p-3 sm:grid-cols-4 lg:grid-cols-8">
              {FUNNEL_STAGES.map((s) => (
                <div key={s} data-testid={`sales-funnel-${s}`} className="rounded-lg border border-fundio-border bg-white px-3 py-2.5">
                  <p className="font-display text-xl font-semibold text-fundio-ink">{d.pipeline[s] ?? 0}</p>
                  <p className="mt-0.5 text-[11px] text-fundio-muted">{stageLabel(s)}</p>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title={t("sales.dashboard.commercialTitle")} subtitle={t("sales.dashboard.commercialSub")} testid="sales-commercial-panel">
            <div className="grid grid-cols-2 gap-3 p-3 sm:grid-cols-4">
              <Kpi testid="sales-kpi-paying" label={t("sales.dashboard.paying")} value={d.paying_customers} />
              <Kpi testid="sales-kpi-mrr" label={t("sales.dashboard.mrr")} value={formatChf(d.mrr_chf)} accent="text-fundio-gold-deep" />
              <Kpi testid="sales-kpi-arpu" label={t("sales.dashboard.arpu")} value={formatChf(d.arpu_chf)} />
              <Kpi testid="sales-kpi-conversion" label={t("sales.dashboard.conversion")} value={`${d.paid_conversion_pct} %`} />
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
